import { useEffect, useState } from "react";
import { fetchLanes, laneAction } from "../room/api";
import type { LaneRow } from "../room/api";
import { SidePanel } from "../components/SidePanel";
import { TerminalPanel } from "../room/TerminalPanel";

function relTime(ts: string | null): string {
  if (!ts) return "—";
  try {
    const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  } catch {
    return "—";
  }
}

type StatusClass = {
  badge: string;
  dot: string;
};

function statusStyle(status: string): StatusClass {
  switch (status) {
    case "idle":
    case "done":
      return { badge: "bg-emerald-950 text-emerald-400 border-emerald-700", dot: "bg-good" };
    case "working":
    case "embodied":
      return { badge: "bg-amber-950 text-amber-300 border-amber-600", dot: "bg-warn" };
    case "stuck":
    case "error":
    case "dead":
      return { badge: "bg-red-950 text-red-400 border-red-700", dot: "bg-bad" };
    default:
      return { badge: "bg-ink/60 text-mist border-edge", dot: "bg-mist" };
  }
}

function LaneCard({
  row,
  onOpenTerminal,
  onEmbodyToggle,
}: {
  row: LaneRow;
  onOpenTerminal: (lane: string) => void;
  onEmbodyToggle: (lane: string, isEmbodied: boolean) => void;
}) {
  const style = statusStyle(row.status);
  const isEmbodied = row.status === "embodied";

  return (
    <div className={`bg-panel border ${style.badge} rounded-lg p-3 flex flex-col gap-2 relative`}>
      {/* Header row */}
      <div className="flex items-center gap-2">
        <span className="text-xl leading-none">{row.emoji || "🤖"}</span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate">{row.name}</div>
          <div className="text-[10px] text-mist truncate">{row.role}</div>
        </div>
        {row.inbox_unread > 0 && (
          <span className="bg-bad text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 shrink-0">
            {row.inbox_unread}
          </span>
        )}
      </div>

      {/* Status badge + last active */}
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
        <span className="text-[11px] font-mono capitalize">{row.status}</span>
        <span className="ml-auto text-[10px] text-mist">{relTime(row.last_active)}</span>
      </div>

      {/* Action buttons */}
      <div className="flex gap-1.5 mt-1">
        <button
          onClick={() => onOpenTerminal(row.name)}
          className="flex-1 text-xs border border-edge rounded px-2 py-1 hover:border-mist transition"
        >
          terminal
        </button>
        <button
          onClick={() => onEmbodyToggle(row.name, isEmbodied)}
          className={`text-xs rounded px-2 py-1 transition border ${
            isEmbodied
              ? "bg-amber-700/50 border-amber-500 text-amber-200"
              : "border-edge hover:border-mist"
          }`}
        >
          {isEmbodied ? "LIVE" : "EMBODY"}
        </button>
      </div>
    </div>
  );
}

export function FleetTab() {
  const [lanes, setLanes] = useState<LaneRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openTerminal, setOpenTerminal] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchLanes()
      .then((data) => {
        setLanes(data);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Failed to load lanes");
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15_000);
    return () => clearInterval(id);
  }, []);

  const handleEmbodyToggle = async (lane: string, isEmbodied: boolean) => {
    try {
      await laneAction(lane, isEmbodied ? "disembody" : "embody");
      // Reload after a short delay to pick up status change
      setTimeout(load, 800);
    } catch {
      // Silent — optimistic UI not needed here
    }
  };

  const embodiedCount = lanes.filter((l) => l.status === "embodied").length;
  const totalCount = lanes.length;

  return (
    <div className="space-y-4">
      {/* Fleet header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-mist uppercase tracking-wide">Fleet</h2>
        <div className="flex items-center gap-3 text-xs text-mist">
          <span>
            <span className="text-amber-400 font-semibold">{embodiedCount}</span>
            {" / "}
            <span className="text-white">{totalCount}</span>
            {" embodied"}
          </span>
          <button
            onClick={load}
            className="border border-edge rounded px-2 py-0.5 hover:border-mist transition"
          >
            refresh
          </button>
        </div>
      </div>

      {/* Content */}
      {loading && lanes.length === 0 ? (
        <div className="text-mist text-sm">Loading lanes…</div>
      ) : error ? (
        <div className="text-bad text-sm">{error}</div>
      ) : lanes.length === 0 ? (
        <div className="text-mist text-sm">No lanes found.</div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {lanes.map((row) => (
            <LaneCard
              key={row.name}
              row={row}
              onOpenTerminal={setOpenTerminal}
              onEmbodyToggle={handleEmbodyToggle}
            />
          ))}
        </div>
      )}

      {/* Terminal side panel */}
      {openTerminal && (
        <SidePanel
          title={`Terminal — ${openTerminal}`}
          onClose={() => setOpenTerminal(null)}
          width={680}
        >
          <div className="relative -m-4">
            <TerminalPanel lane={openTerminal} onClose={() => setOpenTerminal(null)} />
          </div>
        </SidePanel>
      )}
    </div>
  );
}
